import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  deriveCampaignKind, buildHumanRule, fmtDate, toLocalDateInput, fromLocalDateInput, daysLeft,
  normalizeTargetType, TARGET_TYPES,
} from './campaign/campaignUtils';
import { CampaignTypeBadge } from './campaign/CampaignTypeBadge';
import { CampaignGeneralTab } from './campaign/CampaignGeneralTab';
import { CampaignProductTargetTab } from './campaign/CampaignProductTargetTab';
import { CampaignCustomersTab } from './campaign/CampaignCustomersTab';
import { CampaignRestrictionsTab } from './campaign/CampaignRestrictionsTab';
import { CampaignStatsTab } from './campaign/CampaignStatsTab';
import { CampaignCombinationsModal } from './campaign/CampaignCombinationsModal';
import './CampaignDetailPage.css';

const API_BASE = '/api/campaigns';

const EMPTY_FORM = {
  code: '', name: '', shortDescription: '', type: 'DISCOUNT_PCT', conditionUnit: 'QUANTITY',
  applicableProductIdsCsv: '', applicableGroupId: null, applicableSubGroupId: null,
  applicableGroupIdsCsv: '', applicableSubGroupIdsCsv: '', applicableDynamicGroupIdsCsv: '', freeProductIdsCsv: '',
  thresholdQty: 0, thresholdAmount: 0, freeQty: 0, discountPct: 0, discountAmount: 0,
  maxApplications: 0, tiersCsv: '', isPreConditional: false, quantityUnit: 'Adet',
  distributorId: null, distributorIdsCsv: '', customerTargetType: 'ALL', targetCustomerIdsCsv: '', targetCustomerGroupIdsCsv: '', targetCustomerSubGroupIdsCsv: '', targetCustomerDynamicGroupIdsCsv: '', excludedCustomerIdsCsv: '',
  maxPerCustomer: 0, maxPerDistributor: 0, maxPerDay: 0, budgetLimit: 0,
  isCombinable: true, priority: 0, conflictingCampaignIdsCsv: '',
  startDate: '', endDate: '', isActive: true,
  createdBy: '', createdDate: null, updatedDate: null,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

// CSV'nin ilk kimliğini döndürür (eski tekil alanları senkron tutmak için).
function firstCsvId(csv) {
  if (!csv) return null;
  const first = String(csv).split(',').map((x) => x.trim()).filter(Boolean)[0];
  return first || null;
}

function rowToForm(r) {
  return {
    code: pick(r, 'code', 'Code') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    shortDescription: pick(r, 'shortDescription', 'ShortDescription') ?? '',
    type: pick(r, 'type', 'Type') ?? 'DISCOUNT_PCT',
    conditionUnit: pick(r, 'conditionUnit', 'ConditionUnit') ?? 'QUANTITY',
    quantityUnit: pick(r, 'quantityUnit', 'QuantityUnit') ?? 'Adet',
    distributorIdsCsv: pick(r, 'distributorIdsCsv', 'DistributorIdsCsv') ?? '',
    applicableProductIdsCsv: pick(r, 'applicableProductIdsCsv', 'ApplicableProductIdsCsv') ?? '',
    applicableGroupId: pick(r, 'applicableGroupId', 'ApplicableGroupId') ?? null,
    applicableSubGroupId: pick(r, 'applicableSubGroupId', 'ApplicableSubGroupId') ?? null,
    applicableGroupIdsCsv: pick(r, 'applicableGroupIdsCsv', 'ApplicableGroupIdsCsv') ?? '',
    applicableSubGroupIdsCsv: pick(r, 'applicableSubGroupIdsCsv', 'ApplicableSubGroupIdsCsv') ?? '',
    applicableDynamicGroupIdsCsv: pick(r, 'applicableDynamicGroupIdsCsv', 'ApplicableDynamicGroupIdsCsv') ?? '',
    freeProductIdsCsv: pick(r, 'freeProductIdsCsv', 'FreeProductIdsCsv') ?? '',
    thresholdQty: pick(r, 'thresholdQty', 'ThresholdQty') ?? 0,
    thresholdAmount: pick(r, 'thresholdAmount', 'ThresholdAmount') ?? 0,
    freeQty: pick(r, 'freeQty', 'FreeQty') ?? 0,
    discountPct: pick(r, 'discountPct', 'DiscountPct') ?? 0,
    discountAmount: pick(r, 'discountAmount', 'DiscountAmount') ?? 0,
    maxApplications: pick(r, 'maxApplications', 'MaxApplications') ?? 0,
    tiersCsv: pick(r, 'tiersCsv', 'TiersCsv') ?? '',
    isPreConditional: pick(r, 'isPreConditional', 'IsPreConditional') ?? false,
    distributorId: pick(r, 'distributorId', 'DistributorId') ?? null,
    customerTargetType: normalizeTargetType(pick(r, 'customerTargetType', 'CustomerTargetType') ?? 'ALL'),
    targetCustomerIdsCsv: pick(r, 'targetCustomerIdsCsv', 'TargetCustomerIdsCsv') ?? '',
    targetCustomerGroupIdsCsv: pick(r, 'targetCustomerGroupIdsCsv', 'TargetCustomerGroupIdsCsv') ?? '',
    targetCustomerSubGroupIdsCsv: pick(r, 'targetCustomerSubGroupIdsCsv', 'TargetCustomerSubGroupIdsCsv') ?? '',
    targetCustomerDynamicGroupIdsCsv: pick(r, 'targetCustomerDynamicGroupIdsCsv', 'TargetCustomerDynamicGroupIdsCsv') ?? '',
    excludedCustomerIdsCsv: pick(r, 'excludedCustomerIdsCsv', 'ExcludedCustomerIdsCsv') ?? '',
    maxPerCustomer: pick(r, 'maxPerCustomer', 'MaxPerCustomer') ?? 0,
    maxPerDistributor: pick(r, 'maxPerDistributor', 'MaxPerDistributor') ?? 0,
    maxPerDay: pick(r, 'maxPerDay', 'MaxPerDay') ?? 0,
    budgetLimit: pick(r, 'budgetLimit', 'BudgetLimit') ?? 0,
    isCombinable: pick(r, 'isCombinable', 'IsCombinable') ?? true,
    priority: pick(r, 'priority', 'Priority') ?? 0,
    conflictingCampaignIdsCsv: pick(r, 'conflictingCampaignIdsCsv', 'ConflictingCampaignIdsCsv') ?? '',
    startDate: toLocalDateInput(pick(r, 'startDate', 'StartDate')),
    endDate: toLocalDateInput(pick(r, 'endDate', 'EndDate')),
    isActive: pick(r, 'isActive', 'IsActive') ?? true,
    createdBy: pick(r, 'createdBy', 'CreatedBy') ?? '',
    createdDate: pick(r, 'createdDate', 'CreatedDate') ?? null,
    updatedDate: pick(r, 'updatedDate', 'UpdatedDate') ?? null,
  };
}

const TABS = [
  { key: 'general', label: 'Genel', icon: '🗎' },
  { key: 'products', label: 'Ürün Hedefi', icon: '🎯' },
  { key: 'customers', label: 'Müşteriler', icon: '☺' },
  { key: 'restrictions', label: 'Kısıtlamalar', icon: '⚙' },
  { key: 'stats', label: 'İstatistikler', icon: '📈' },
];

export function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  usePageMeta(isNew ? 'Yeni Kampanya' : 'Kampanya Detay', 'Promosyon tanımı ve kuralları.');

  // Menü aksiyon hakları — aktif route'tan menuId çözülür. Yeni kayıt Ekle, mevcut kayıt
  // Güncelle hakkı ister; hak çözülemezse altyapı default-open döner (davranış değişmez).
  const { canAdd, canEdit, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [isEdit, setIsEdit] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [showCombos, setShowCombos] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kampanya alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setForm(rowToForm(data));
    } catch (err) {
      showToast('error', err.message || 'Kampanya alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const buildBody = () => ({
    code: form.code.trim(),
    name: form.name.trim(),
    shortDescription: form.shortDescription?.trim() || null,
    type: form.type,
    conditionUnit: form.conditionUnit,
    quantityUnit: form.quantityUnit || 'Adet',
    applicableProductIdsCsv: form.applicableProductIdsCsv || null,
    applicableGroupIdsCsv: form.applicableGroupIdsCsv || null,
    applicableSubGroupIdsCsv: form.applicableSubGroupIdsCsv || null,
    applicableDynamicGroupIdsCsv: form.applicableDynamicGroupIdsCsv || null,
    // Eski tekil alanlar geriye dönük uyum için CSV'nin ilk değeriyle senkron tutulur.
    applicableGroupId: firstCsvId(form.applicableGroupIdsCsv) || form.applicableGroupId || null,
    applicableSubGroupId: firstCsvId(form.applicableSubGroupIdsCsv) || form.applicableSubGroupId || null,
    freeProductIdsCsv: form.freeProductIdsCsv || null,
    thresholdQty: Number(form.thresholdQty) || 0,
    thresholdAmount: Number(form.thresholdAmount) || 0,
    freeQty: Number(form.freeQty) || 0,
    discountPct: Number(form.discountPct) || 0,
    discountAmount: Number(form.discountAmount) || 0,
    maxApplications: Number(form.maxApplications) || 0,
    tiersCsv: form.tiersCsv || null,
    isPreConditional: !!form.isPreConditional,
    distributorIdsCsv: form.distributorIdsCsv || null,
    distributorId: firstCsvId(form.distributorIdsCsv) || form.distributorId || null,
    customerTargetType: form.customerTargetType || 'ALL',
    targetCustomerIdsCsv: form.targetCustomerIdsCsv || null,
    targetCustomerGroupIdsCsv: form.targetCustomerGroupIdsCsv || null,
    targetCustomerSubGroupIdsCsv: form.targetCustomerSubGroupIdsCsv || null,
    targetCustomerDynamicGroupIdsCsv: form.targetCustomerDynamicGroupIdsCsv || null,
    excludedCustomerIdsCsv: form.excludedCustomerIdsCsv || null,
    maxPerCustomer: Number(form.maxPerCustomer) || 0,
    maxPerDistributor: Number(form.maxPerDistributor) || 0,
    maxPerDay: Number(form.maxPerDay) || 0,
    budgetLimit: Number(form.budgetLimit) || 0,
    isCombinable: !!form.isCombinable,
    priority: Number(form.priority) || 0,
    conflictingCampaignIdsCsv: form.conflictingCampaignIdsCsv || null,
    startDate: fromLocalDateInput(form.startDate),
    endDate: fromLocalDateInput(form.endDate),
    isActive: !!form.isActive,
  });

  const handleSave = async () => {
    if (!form.code.trim()) { showToast('error', 'Kod zorunlu.'); setActiveTab('general'); return; }
    if (!form.name.trim()) { showToast('error', 'Ad zorunlu.'); setActiveTab('general'); return; }
    setSaving(true);
    try {
      const url = isNew ? API_BASE : `${API_BASE}/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(buildBody()) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', isNew ? 'Kampanya oluşturuldu.' : 'Değişiklikler kaydedildi.');
      if (isNew) {
        const data = pick(payload, 'Data', 'data') ?? payload;
        const newId = pick(data, 'id', 'Id');
        if (newId) { navigate(`/campaigns/${newId}`, { replace: true }); return; }
      }
      setIsEdit(false);
      load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const kind = deriveCampaignKind(form);
  const dleft = daysLeft(form.endDate ? fromLocalDateInput(form.endDate) : null);

  // Sekmelerin içindeki tüm yazma afordansları (ürün/müşteri satırı ekle-çıkar, alan girişi)
  // isEdit'e bakıyor; kayıt gövdesini değiştirdikleri için kaydetme hakkına da bağlanır.
  const tabProps = { form, setForm, isEdit: isEdit && canSave };

  return (
    <div style={s.container}>
      <div className="cmp-detail">
        {/* Header */}
        <div className="cmp-detail-header">
          <button style={s.btnSecondary} onClick={() => navigate('/campaigns')}>← Geri</button>
          <div className="cmp-detail-title">
            <span aria-hidden="true">🎁</span>
            {form.name || (isNew ? 'Yeni Kampanya' : '...')}
            {!isNew && (form.isActive
              ? <StatusBadge tone="ok">Aktif</StatusBadge>
              : <StatusBadge tone="neutral">Pasif</StatusBadge>)}
          </div>
          <div className="cmp-detail-header-actions">
            {canCopy && canAdd && !isEdit && !isNew && <button style={s.btnSecondary} onClick={() => navigate('/campaigns/new')}>Kopyala-Yeni</button>}
            {isEdit
              ? (
                <>
                  {!isNew && <button style={s.btnSecondary} onClick={() => { setIsEdit(false); load(); }} disabled={saving}>Vazgeç</button>}
                  {canSave && (
                    <button style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                      {saving ? 'Kaydediliyor...' : (isNew ? 'Oluştur' : 'Değişiklikleri Kaydet')}
                    </button>
                  )}
                </>
              )
              : (canEdit && <button style={s.btnPrimary} onClick={() => setIsEdit(true)}>Düzenle</button>)}
          </div>
        </div>

        {loading ? (
          <div className="cmp-empty">Yükleniyor…</div>
        ) : (
          <div className="cmp-detail-body">
            {/* Sol ray */}
            <div className="cmp-rail">
              <div className="cmp-rail-card">
                <h4>Durum Bilgisi</h4>
                <div className="cmp-rail-grid">
                  <div className="cmp-rail-stat"><div className="v">{form.isActive ? 'Aktif' : 'Pasif'}</div><div className="l">Durum</div></div>
                  <div className="cmp-rail-stat"><div className="v">{dleft != null ? `${dleft}` : '—'}</div><div className="l">Gün Kaldı</div></div>
                  <div className="cmp-rail-stat"><div className="v" style={{ color: '#2563eb' }}>{fmtDate(fromLocalDateInput(form.startDate))}</div><div className="l">Başlangıç</div></div>
                  <div className="cmp-rail-stat"><div className="v" style={{ color: '#d97706' }}>{fmtDate(fromLocalDateInput(form.endDate))}</div><div className="l">Bitiş</div></div>
                </div>
              </div>
              <div className="cmp-rail-card">
                <h4>Promosyon Özeti</h4>
                <div style={{ marginBottom: 10 }}><CampaignTypeBadge kind={kind} /></div>
                <div className="cmp-rail-row"><span className="k">Kampanya Koşulu</span></div>
                <div style={{ background: 'var(--bg-secondary,#f7f7f5)', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 600 }}>
                  {buildHumanRule(form)}
                </div>
                <div className="cmp-rail-row" style={{ marginTop: 8 }}>
                  <span className="k">Hedef</span>
                  <span>{(TARGET_TYPES.find((t) => t.value === normalizeTargetType(form.customerTargetType)) || {}).label || 'Tüm Müşteriler'}</span>
                </div>
                <button style={{ ...s.btnSecondary, width: '100%', marginTop: 10 }} onClick={() => setShowCombos(true)}>
                  Kombinasyonları Göster
                </button>
              </div>
            </div>

            {/* Sekme paneli */}
            <div className="cmp-panel">
              <div className="cmp-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    className={`cmp-tab${activeTab === t.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    <span aria-hidden="true" style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
                  </button>
                ))}
              </div>
              <div className="cmp-tab-body">
                {activeTab === 'general' && <CampaignGeneralTab {...tabProps} />}
                {activeTab === 'products' && <CampaignProductTargetTab {...tabProps} />}
                {activeTab === 'customers' && <CampaignCustomersTab {...tabProps} />}
                {activeTab === 'restrictions' && <CampaignRestrictionsTab {...tabProps} />}
                {activeTab === 'stats' && <CampaignStatsTab campaignId={isNew ? null : id} form={form} />}
              </div>
            </div>
          </div>
        )}
      </div>

      {showCombos && (
        <CampaignCombinationsModal
          campaignId={isNew ? null : id}
          form={form}
          onClose={() => setShowCombos(false)}
          onGoToRestrictions={() => { setShowCombos(false); setActiveTab('restrictions'); setIsEdit(true); }}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CampaignDetailPage;
